import React, { useRef, useEffect } from 'react'
import styled from '@emotion/styled'
import {
    useProductLayout,
    LayoutOption,
} from '../../context/ProductLayoutContext'
import { GridIcon, ListIcon } from '../icons/Icons'
import { PRIMARY_PAGE_TEXT_COLOR_VAR } from '../../context/CustomizationContextProvider'
import { SectionMedia } from '../../style/scrollingLayout'

const ToggleContainer = styled.div<{ headerOffset?: number }>(
    ({ headerOffset }) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        borderRadius: 0,
        overflow: 'hidden',
        height: 32,
        padding: 0,
        border: 'none',
        background: 'transparent',
        boxShadow: 'none',
        scrollMarginTop: headerOffset || 150,
        // Section width picks columns from here; toggle would be inert.
        [SectionMedia.MD]: {
            display: 'none',
        },
    }),
)

interface ToggleButtonProps {
    isActive: boolean
}

const ToggleButton = styled.button<ToggleButtonProps>`
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.15s ease;
    height: 100%;
    min-width: 24px;
    color: var(${PRIMARY_PAGE_TEXT_COLOR_VAR});
    opacity: ${(props) => (props.isActive ? 1 : 0.35)};
    box-shadow: none;

    &:hover {
        opacity: ${(props) => (props.isActive ? 1 : 0.7)};
    }

    &:focus {
        outline: none;
    }
`
const DEFAULT_PRODUCT_COUNT_THRESHOLD = 1

export const ProductLayoutToggle: React.FC<{
    productCount: number
    headerOffset?: number
}> = ({ productCount, headerOffset = 150 }) => {
    const { layoutOption, setLayoutOption } = useProductLayout()
    const toggleRef = useRef<HTMLDivElement | null>(null)
    const shouldScrollRef = useRef(false)

    const handleLayoutChange = (option: LayoutOption) => {
        // Mark that we should scroll after React re-renders
        shouldScrollRef.current = true
        // Update the layout - this triggers React re-render
        setLayoutOption(option)
    }

    // Scroll after React completes re-render and DOM is updated
    // This useEffect runs after layoutOption changes and React has updated the DOM
    useEffect(() => {
        if (shouldScrollRef.current && toggleRef.current) {
            // Use requestAnimationFrame to ensure DOM layout is fully calculated
            requestAnimationFrame(() => {
                toggleRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                })
                shouldScrollRef.current = false
            })
        }
    }, [layoutOption])

    if (productCount < DEFAULT_PRODUCT_COUNT_THRESHOLD) {
        return null
    }

    return (
        <ToggleContainer
            ref={toggleRef}
            headerOffset={headerOffset}
            role="group"
            aria-label="Product layout options"
        >
            <ToggleButton
                isActive={layoutOption === 'grid'}
                onClick={() => handleLayoutChange('grid')}
                aria-label="Grid view (2 columns)"
                aria-pressed={layoutOption === 'grid'}
                type="button"
            >
                <GridIcon width={18} height={18} />
            </ToggleButton>
            <ToggleButton
                isActive={layoutOption === 'list'}
                onClick={() => handleLayoutChange('list')}
                aria-label="List view (1 column)"
                aria-pressed={layoutOption === 'list'}
                type="button"
            >
                <ListIcon width={18} height={18} />
            </ToggleButton>
        </ToggleContainer>
    )
}
